import countries, { CountryIso2 } from '~/constants/countries';
import { BaseAddress, JSONBaseAddress } from '~/types/Address';

export interface JSONAPIAddress extends JSONBaseAddress {
  id?: number;
  is_default_sender?: boolean;
  is_default_return?: boolean;
  object_created?: Date;
  object_updated?: Date;
  store_address_id?: Maybe<string>;
  store_platform_names?: Maybe<string[]>;
}

export interface SavedAPIAddress extends BaseAddress {
  id: number;
  isDefaultSender?: boolean;
  isDefaultReturn?: boolean;
  objectCreated?: Date;
  objectUpdated?: Date;
  storeAddressId?: Maybe<string>;
  storePlatformNames?: Maybe<string[]>;
  objectOwnerID?: number;
  addressId?: string;
}

export type APIAddress = OptionalId<SavedAPIAddress>;

export function fromJSON(jsonAddress: JSONAPIAddress): SavedAPIAddress;
export function fromJSON(jsonAPIAddress: null | undefined): null;
export function fromJSON(jsonAddress: null | undefined | JSONAPIAddress): SavedAPIAddress | null;
export function fromJSON(
  jsonAPIAddress: null | undefined | JSONAPIAddress,
): SavedAPIAddress | null {
  if (!jsonAPIAddress) return null;

  return {
    company: jsonAPIAddress.company,
    street1: jsonAPIAddress.street1,
    street2: jsonAPIAddress.street2,
    streetNumber: jsonAPIAddress.street_no,
    city: jsonAPIAddress.city,
    state: jsonAPIAddress.state,
    zip: jsonAPIAddress.zip,
    country: countries[jsonAPIAddress.country as CountryIso2],
    name: jsonAPIAddress.name,
    phone: jsonAPIAddress.phone,
    email: jsonAPIAddress.email,
    id: jsonAPIAddress.id!, // Assuming data is from server, so address was saved
    isDefaultSender: jsonAPIAddress.is_default_sender ? jsonAPIAddress.is_default_sender : false,
    isDefaultReturn: jsonAPIAddress.is_default_return ? jsonAPIAddress.is_default_return : false,
    objectCreated: jsonAPIAddress.object_created
      ? new Date(jsonAPIAddress.object_created)
      : undefined,
    objectUpdated: jsonAPIAddress.object_updated
      ? new Date(jsonAPIAddress.object_updated)
      : undefined,
    storeAddressId: jsonAPIAddress.store_address_id ? jsonAPIAddress.store_address_id : null,
    storePlatformNames: jsonAPIAddress.store_platform_names
      ? jsonAPIAddress.store_platform_names
      : null,
  };
}

export function toJSON(address: APIAddress): Maybe<JSONAPIAddress> {
  if (!address) return null;

  return {
    id: address.id,
    company: address.company,
    street1: address.street1,
    street2: address.street2,
    street_no: address.streetNumber,
    city: address.city,
    state: address.state,
    zip: address.zip,
    country: address.country ? address.country.iso2 : '',
    name: address.name,
    phone: address.phone ? address.phone : '',
    email: address.email,
    is_default_return: address.isDefaultReturn ? address.isDefaultReturn : false,
    is_default_sender: address.isDefaultSender ? address.isDefaultSender : false,
  };
}

export function newAPIAddress(address: BaseAddress): APIAddress {
  const { company, name, street1, streetNumber, street2, city, state, country, zip, phone, email } =
    address;

  const newAPIAddress: APIAddress = {
    name: name || '',
    company: company || '',
    streetNumber: streetNumber || '',
    street1: street1 || '',
    street2: street2 || '',
    city: city || '',
    state: state || '',
    zip: zip || '',
    country: country || null,
    phone: phone || '',
    email: email || '',
  };

  return newAPIAddress;
}

export default APIAddress;
